// Модал «Добавить из источника»: описание, ссылка или скриншот — распознаёт
// сервер (через ИИ-провайдера), а для «Описания» без ИИ (недоступен/выключен/гость)
// работает прежний локальный parseInput(). Экспортируется под старым именем
// ParseModal — так его до сих пор импортирует LibraryPanel.
import { useEffect, useRef, useState } from 'react';
import type { FlightSegment, SegmentOrigin } from '../../types';
import { useActions, newId } from '../../store';
import { money, toMinor } from '../../core';
import { parseInput } from '../../parser';
import { useT } from '../../i18n';
import type { Translate } from '../../i18n';
import type { ApiError } from '../../auth';
import { Modal } from '../shell/Modal';
import { DraftCard } from './DraftCard';
import { draftToForm, aiDraftToForm, validateDraftForm, hasBlockingErrors } from './draftForm';
import type { DraftForm } from './draftForm';
import { fetchRecognizeConfig, recognize } from './api';
import type { AiSource, RecognizeConfig } from './api';
import { prepareImage, formatBytes, MAX_IMAGES, MAX_TOTAL_BYTES } from './image';
import styles from './SourceModal.module.css';

export interface SourceModalProps {
  open: boolean;
  onClose(): void;
}

const TABS: readonly AiSource[] = ['text', 'url', 'screenshot'];
const URL_RE = /^https?:\/\//i;

/** Коды из SPEC.md — всё остальное (сеть недоступна, неопознанный http_NNN) уходит в recog.err.unknown. */
const KNOWN_ERROR_CODES = new Set([
  'unauthorized',
  'disabled',
  'no_model',
  'no_key',
  'bad_request',
  'too_large',
  'rate_limited',
  'timeout',
  'provider',
  'bad_response',
  'fetch_url',
  'unknown',
]);

function errorMessage(t: Translate, err: ApiError): string {
  const code = KNOWN_ERROR_CODES.has(err.error) ? err.error : 'unknown';
  const params = code === 'provider' ? { message: err.message ?? '' } : undefined;
  return t(`recog.err.${code}`, params);
}

/** Есть ли в черновиках хоть что-то распознанное — критерий «локальный разбор нашёл результат». */
function localFoundSomething(rawInput: string): boolean {
  const res = parseInput(rawInput, { year: new Date().getFullYear() });
  return res.drafts.some((d) => d.from || d.to || d.departLocal);
}

/** Готовый к отправке скриншот. id нужен только React-списку — dataUrl в ключе был бы мегабайтом строки. */
interface Shot {
  id: string;
  dataUrl: string;
  bytes: number;
}

let shotSeq = 0;
function nextShotId(): string {
  shotSeq += 1;
  return `shot-${shotSeq}`;
}

interface ReviewState {
  origin: SegmentOrigin;
  forms: DraftForm[];
  /** id модели провайдера; null — разобрано локальным парсером (подпись recog.local). */
  model: string | null;
  /** Заметки уровня всего ответа (recog.notes) — отдельно от form.notes у каждой карточки. */
  notes: string[];
  /** ИИ не ответил на «Описании», но локальный разбор нашёл результат — recog.fallback. */
  fallback: boolean;
}

function buildLocalReview(rawInput: string, fallback: boolean): ReviewState {
  const res = parseInput(rawInput, { year: new Date().getFullYear() });
  return {
    origin: 'text',
    forms: res.drafts.map(draftToForm),
    model: null,
    notes: res.ok ? [] : res.errors,
    fallback,
  };
}

export function SourceModal({ open, onClose }: SourceModalProps) {
  const actions = useActions();
  const t = useT();

  const [tab, setTab] = useState<AiSource>('text');
  const tabRefs = useRef<Partial<Record<AiSource, HTMLButtonElement | null>>>({});

  // Конфиг доступности ИИ по источникам — тянется один раз при открытии.
  const [config, setConfig] = useState<RecognizeConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  // 401 у гостя — штатный случай, не ошибка: другой текст баннера, никакого alert.
  const [isGuest, setIsGuest] = useState(false);

  const [rawText, setRawText] = useState('');
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);
  const [shots, setShots] = useState<Shot[]>([]);
  // Разбор файлов асинхронный, а событий — несколько (перетащили, потом
  // вставили из буфера): считать «сколько уже есть» по состоянию из замыкания
  // значило бы затирать пачку, добавленную соседним обработчиком.
  const shotsRef = useRef<Shot[]>([]);
  const [imageErrors, setImageErrors] = useState<string[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const [review, setReview] = useState<ReviewState | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setConfigLoading(true);
    setIsGuest(false);
    setConfig(null);
    fetchRecognizeConfig().then((res) => {
      if (cancelled) return;
      if (res.ok && res.value) {
        setConfig(res.value);
      } else if (!res.ok && res.error.error === 'unauthorized') {
        setIsGuest(true);
      }
      setConfigLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Модал закрыт или размонтирован — не даём висящему запросу прилететь в никуда.
  useEffect(() => {
    if (!open) abortRef.current?.abort();
    return () => abortRef.current?.abort();
  }, [open]);

  if (!open) return null;

  function reset() {
    setTab('text');
    setRawText('');
    setUrl('');
    setUrlError(null);
    putShots([]);
    setImageErrors([]);
    setLoading(false);
    setRunError(null);
    setReview(null);
  }

  function putShots(next: Shot[]) {
    shotsRef.current = next;
    setShots(next);
  }

  function handleClose() {
    abortRef.current?.abort();
    reset();
    onClose();
  }

  function aiAvailable(source: AiSource): boolean {
    if (!config) return false;
    return config.enabled && config.sources[source];
  }

  // «Описание» работает и без ИИ — свой parseInput() локально, поэтому она
  // видна всегда. «Ссылка» и «Скриншот» без ИИ не разбирают вовсе, поэтому
  // вместо баннера-объяснения («recog.needAi») их вкладки просто не рисуются.
  // Пока конфиг не пришёл, показываем только «Описание»: рисовать вкладки, а
  // через мгновение прятать половину из них — хуже, чем один раз спокойно
  // дорисовать оставшиеся, когда конфиг ответит.
  const visibleTabs: readonly AiSource[] = configLoading
    ? (['text'] as const)
    : TABS.filter((s) => s === 'text' || aiAvailable(s));

  function changeTab(next: AiSource) {
    if (tab === next) return;
    // Переключение источника обрывает незавершённый запрос — его результат
    // относился бы уже не к тому, что видит пользователь.
    if (loading) {
      abortRef.current?.abort();
      setLoading(false);
    }
    setRunError(null);
    setTab(next);
  }

  function handleTabKeyDown(e: React.KeyboardEvent) {
    const idx = visibleTabs.indexOf(tab);
    let nextIdx: number | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') nextIdx = (idx + 1) % visibleTabs.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') nextIdx = (idx - 1 + visibleTabs.length) % visibleTabs.length;
    else if (e.key === 'Home') nextIdx = 0;
    else if (e.key === 'End') nextIdx = visibleTabs.length - 1;
    if (nextIdx === null) return;
    e.preventDefault();
    const next = visibleTabs[nextIdx]!;
    changeTab(next);
    tabRefs.current[next]?.focus();
  }

  const maxImages = config?.maxImages ?? MAX_IMAGES;
  const maxTotalBytes = config?.maxTotalImageBytes ?? MAX_TOTAL_BYTES;
  const shotsBytes = shots.reduce((sum, s) => sum + s.bytes, 0);

  function mb(bytes: number): number {
    return Math.round(bytes / (1024 * 1024));
  }

  /** «файл.png: Картинка больше 6 МБ» — с пачкой без имени файла непонятно, о каком речь. */
  function fileError(name: string, message: string): string {
    return name ? `${name}: ${message}` : message;
  }

  /**
   * Добавляет к пачке всё, что выбрал/перетащил/вставил пользователь. Каждый
   * файл проверяется отдельно: одна негодная картинка не должна отменять
   * остальные — она просто попадает в список ошибок под пачкой.
   */
  async function handleFiles(files: File[]) {
    if (files.length === 0) return;
    setImageErrors([]);
    const errors: string[] = [];
    const prepared: Shot[] = [];

    for (const file of files) {
      const res = await prepareImage(file);
      if (!res.ok) {
        errors.push(fileError(file.name, t(res.error.key, res.error.params)));
        continue;
      }
      // Уменьшение через canvas обычно укладывает картинку в любой предел, но
      // деградировать оно умеет (нет canvas, битый файл) — и тогда на сервер
      // ушёл бы оригинал, который тот отвергнет по размеру. Проверяем то, что
      // реально поедет, и предел берём объявленный сервером, а не свой.
      const limit = config?.maxImageBytes;
      if (limit && res.bytes > limit) {
        errors.push(fileError(file.name, t('recog.shot.tooBig', { limit: mb(limit) })));
        continue;
      }
      prepared.push({ id: nextShotId(), dataUrl: res.dataUrl, bytes: res.bytes });
    }

    // Оба предела — сервера: и число картинок, и их общий вес. Отсекаем здесь,
    // чтобы человек увидел «влезло четыре из шести», а не 413 после ожидания.
    const next = [...shotsRef.current];
    let total = next.reduce((sum, s) => sum + s.bytes, 0);
    let overCount = 0;
    let overBytes = 0;
    for (const shot of prepared) {
      if (next.length >= maxImages) {
        overCount += 1;
        continue;
      }
      if (total + shot.bytes > maxTotalBytes) {
        overBytes += 1;
        continue;
      }
      next.push(shot);
      total += shot.bytes;
    }
    if (overCount) errors.push(t('recog.shot.tooMany', { limit: maxImages, count: overCount }));
    if (overBytes) errors.push(t('recog.shot.tooBigTotal', { limit: mb(maxTotalBytes), count: overBytes }));

    putShots(next);
    setImageErrors(errors);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files ? Array.from(e.dataTransfer.files) : [];
    if (files.length) void handleFiles(files);
  }

  // Вставка из буфера ловится на самом модале: фокус может быть где угодно
  // внутри него, а не только в специальном поле для картинки.
  function handlePasteCapture(e: React.ClipboardEvent) {
    if (!aiAvailable('screenshot')) return;
    const items = e.clipboardData ? Array.from(e.clipboardData.items) : [];
    const files = items
      .filter((it) => it.kind === 'file' && it.type.startsWith('image/'))
      .map((it) => it.getAsFile())
      .filter((f): f is File => f !== null);
    if (files.length === 0) return;
    e.preventDefault();
    setTab('screenshot');
    void handleFiles(files);
  }

  // Убрали картинку — жалобы прошлой попытки («лишние не добавлены») больше
  // ни к чему не относятся: в пачке уже другой состав.
  function removeShot(id: string) {
    putShots(shotsRef.current.filter((s) => s.id !== id));
    setImageErrors([]);
  }

  function removeAllShots() {
    putShots([]);
    setImageErrors([]);
  }

  async function handleRun() {
    if (loading) return;
    setRunError(null);

    if (tab === 'text') {
      if (!rawText.trim()) return;
      if (!aiAvailable('text')) {
        setReview(buildLocalReview(rawText, false));
        return;
      }
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      const res = await recognize({ source: 'text', text: rawText }, controller.signal);
      // Отмену просил сам пользователь: показывать ему после этого ошибку сети
      // (а на этой вкладке ещё и результат локального разбора) — значит
      // отвечать не на то действие, которое он совершил.
      if (controller.signal.aborted) return;
      setLoading(false);
      if (res.ok) {
        setReview({
          origin: 'text',
          forms: res.value.drafts.map(aiDraftToForm),
          model: res.value.model,
          notes: res.value.notes,
          fallback: false,
        });
        return;
      }
      // ИИ не ответил — не теряем работу пользователя: если локальный разбор
      // того же текста что-то нашёл, показываем его результат вместо ошибки.
      if (localFoundSomething(rawText)) {
        setReview(buildLocalReview(rawText, true));
        return;
      }
      setRunError(errorMessage(t, res.error));
      return;
    }

    if (tab === 'url') {
      if (!aiAvailable('url')) return;
      const trimmed = url.trim();
      if (!URL_RE.test(trimmed)) {
        setUrlError(t('recog.url.invalid'));
        return;
      }
      setUrlError(null);
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      const res = await recognize({ source: 'url', url: trimmed }, controller.signal);
      if (controller.signal.aborted) return;
      setLoading(false);
      if (res.ok) {
        setReview({
          origin: 'url',
          forms: res.value.drafts.map(aiDraftToForm),
          model: res.value.model,
          notes: res.value.notes,
          fallback: false,
        });
      } else {
        setRunError(errorMessage(t, res.error));
      }
      return;
    }

    // screenshot
    if (!aiAvailable('screenshot') || shots.length === 0) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    const res = await recognize(
      { source: 'screenshot', images: shots.map((s) => s.dataUrl) },
      controller.signal,
    );
    if (controller.signal.aborted) return;
    setLoading(false);
    if (res.ok) {
      setReview({
        origin: 'screenshot',
        forms: res.value.drafts.map(aiDraftToForm),
        model: res.value.model,
        notes: res.value.notes,
        fallback: false,
      });
    } else {
      setRunError(errorMessage(t, res.error));
    }
  }

  function handleCancelRun() {
    abortRef.current?.abort();
    setLoading(false);
  }

  function handleBack() {
    setReview(null);
  }

  function updateForm(i: number, patch: Partial<DraftForm>) {
    setReview((prev) => {
      if (!prev) return prev;
      return { ...prev, forms: prev.forms.map((f, idx) => (idx === i ? { ...f, ...patch } : f)) };
    });
  }

  const errorsList = review ? review.forms.map((f) => validateDraftForm(f, t)) : [];
  const selectedCount = review
    ? review.forms.filter((f, i) => f.include && !hasBlockingErrors(errorsList[i])).length
    : 0;

  function handleSave() {
    if (!review) return;
    const addedAt = Date.now();
    const segments: FlightSegment[] = [];
    review.forms.forEach((f, i) => {
      if (!f.include || hasBlockingErrors(errorsList[i])) return;
      segments.push({
        kind: 'flight',
        id: newId('f'),
        from: f.from.toUpperCase(),
        to: f.to.toUpperCase(),
        fromTz: f.fromTz,
        toTz: f.toTz,
        departLocal: f.departLocal,
        arriveLocal: f.arriveLocal,
        carrier: f.carrier.trim(),
        flightNumber: f.flightNumber.trim() || undefined,
        price: money(toMinor(f.amount), f.currency),
        priceScope: f.priceScope,
        addedAt,
        origin: review.origin,
      });
    });
    if (segments.length === 0) return;
    actions.addSegments(segments);
    handleClose();
  }

  // Условие «есть что распознавать» — своё для каждой вкладки: у «Описания»
  // это непустой текст, у «Ссылки»/«Скриншота» вдобавок нужен доступный ИИ
  // (без него сама вкладка не показывается — см. visibleTabs).
  function canRun(): boolean {
    if (tab === 'text') return Boolean(rawText.trim());
    if (tab === 'url') return aiAvailable('url') && Boolean(url.trim());
    return aiAvailable('screenshot') && shots.length > 0;
  }

  const footer = review ? (
    <>
      <button type="button" className="btn" onClick={handleBack}>
        {t('parse.back')}
      </button>
      <button type="button" className="btn btn-primary" disabled={selectedCount === 0} onClick={handleSave}>
        {t('parse.addSelected', { count: selectedCount })}
      </button>
    </>
  ) : (
    <>
      {/* Пока идёт запрос, «Отмена» обрывает именно его и оставляет модал
          открытым — набранный текст/ссылка/картинка никуда не пропадают.
          В покое та же кнопка закрывает модал целиком (крестик делает то же
          самое). Кнопка запуска — крупная основная, стоит рядом, как и
          остальные пары кнопок футера в проекте (см. ConfirmDialog). */}
      <button type="button" className="btn" onClick={loading ? handleCancelRun : handleClose}>
        {t('form.cancel')}
      </button>
      <button
        type="button"
        className="btn btn-primary"
        disabled={loading || !canRun()}
        aria-busy={loading || undefined}
        onClick={handleRun}
      >
        {loading ? t('recog.running') : t('recog.run')}
      </button>
    </>
  );

  return (
    <Modal title={t('recog.title')} onClose={handleClose} footer={footer} wide>
      <div onPasteCapture={handlePasteCapture}>
        {!review ? (
          <div className={styles.step1}>
            <div
              className={styles.tabs}
              role="tablist"
              aria-label={t('recog.title')}
              onKeyDown={handleTabKeyDown}
            >
              {visibleTabs.map((s) => (
                <button
                  key={s}
                  ref={(el) => {
                    tabRefs.current[s] = el;
                  }}
                  type="button"
                  role="tab"
                  id={`recog-tab-${s}`}
                  aria-selected={tab === s}
                  aria-controls={`recog-panel-${s}`}
                  tabIndex={tab === s ? 0 : -1}
                  className={`${styles.tab} ${tab === s ? styles.tabActive : ''}`}
                  onClick={() => changeTab(s)}
                >
                  {t(`recog.tab.${s}`)}
                </button>
              ))}
            </div>

            {tab === 'text' && (
              <div id="recog-panel-text" role="tabpanel" aria-labelledby="recog-tab-text" className={styles.panel}>
                <label htmlFor="recog-text" className="field-label">
                  {t('recog.text.label')}
                </label>
                <textarea
                  id="recog-text"
                  className={`textarea ${styles.textarea}`}
                  rows={9}
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  placeholder={t('recog.text.placeholder')}
                  autoFocus
                />
                {!configLoading && !aiAvailable('text') && (
                  <p className={styles.offBanner}>{t(isGuest ? 'recog.off.guest' : 'recog.off')}</p>
                )}
                <p className={styles.hint}>{t(aiAvailable('text') ? 'recog.privacy.ai' : 'recog.privacy.local')}</p>
                {runError && (
                  <p className={styles.errorBanner} role="alert">
                    {runError}
                  </p>
                )}
              </div>
            )}

            {tab === 'url' && (
              <div id="recog-panel-url" role="tabpanel" aria-labelledby="recog-tab-url" className={styles.panel}>
                <label htmlFor="recog-url" className="field-label">
                  {t('recog.url.label')}
                </label>
                <input
                  id="recog-url"
                  className="input"
                  type="url"
                  inputMode="url"
                  value={url}
                  disabled={!aiAvailable('url')}
                  aria-invalid={urlError ? true : undefined}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    setUrlError(null);
                  }}
                  placeholder={t('recog.url.placeholder')}
                />
                {urlError && <p className="field-error">{urlError}</p>}
                <p className={styles.hint}>{t('recog.privacy.ai')}</p>
                {runError && (
                  <p className={styles.errorBanner} role="alert">
                    {runError}
                  </p>
                )}
              </div>
            )}

            {tab === 'screenshot' && (
              <div
                id="recog-panel-screenshot"
                role="tabpanel"
                aria-labelledby="recog-tab-screenshot"
                className={styles.panel}
              >
                <span className="field-label">{t('recog.shot.label')}</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  // image/* вместо белого списка: HEIC с айфона и прочую
                  // экзотику браузер пережмёт в JPEG сам (см. prepareImage), а
                  // узкий accept просто не давал такие файлы выбрать.
                  accept="image/*"
                  multiple
                  className="visually-hidden"
                  aria-label={t('recog.shot.pick')}
                  tabIndex={-1}
                  onChange={(e) => {
                    const files = e.target.files ? Array.from(e.target.files) : [];
                    if (files.length) void handleFiles(files);
                    // Сбрасываем value: повторный выбор того же файла (после «Убрать») тоже должен сработать.
                    e.target.value = '';
                  }}
                />
                {shots.length === 0 ? (
                  <div
                    className={`${styles.dropzone} ${dragOver ? styles.dropzoneOver : ''}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                  >
                    <p>{t('recog.shot.drop')}</p>
                    <button
                      type="button"
                      className="btn"
                      disabled={!aiAvailable('screenshot')}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {t('recog.shot.pick')}
                    </button>
                  </div>
                ) : (
                  <div
                    className={`${styles.shots} ${dragOver ? styles.shotsOver : ''}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                  >
                    <ul className={styles.shotList}>
                      {shots.map((shot, i) => (
                        <li key={shot.id} className={styles.shot}>
                          {/* Декоративное превью: номер и подпись рядом уже всё говорят. */}
                          <img src={shot.dataUrl} alt="" className={styles.shotImg} />
                          <span className={styles.shotNum}>{i + 1}</span>
                          <button
                            type="button"
                            className={styles.shotRemove}
                            aria-label={t('recog.shot.removeOne', { num: i + 1 })}
                            onClick={() => removeShot(shot.id)}
                          >
                            ×
                          </button>
                        </li>
                      ))}
                    </ul>
                    <div className={styles.previewMeta}>
                      <p>
                        {t('recog.shot.ready', {
                          count: shots.length,
                          limit: maxImages,
                          size: formatBytes(shotsBytes),
                        })}
                      </p>
                      <div className={styles.previewActions}>
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={shots.length >= maxImages}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          {t('recog.shot.add')}
                        </button>
                        <button type="button" className="btn btn-sm btn-danger" onClick={removeAllShots}>
                          {t('recog.shot.remove')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {imageErrors.length > 0 && (
                  <ul className={styles.shotErrors} role="alert">
                    {imageErrors.map((msg, i) => (
                      <li key={i} className="field-error">
                        {msg}
                      </li>
                    ))}
                  </ul>
                )}
                <p className={styles.hint}>{t('recog.shot.multi')}</p>
                <p className={styles.hint}>{t('recog.privacy.ai')}</p>
                {runError && (
                  <p className={styles.errorBanner} role="alert">
                    {runError}
                  </p>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className={styles.step2}>
            <p className={styles.reviewCaption}>{t('recog.review')}</p>
            <p className={styles.modelLine}>
              {review.model ? t('recog.model', { model: review.model }) : t('recog.local')}
            </p>
            {review.fallback && <p className={styles.fallbackBanner}>{t('recog.fallback')}</p>}
            {review.notes.length > 0 && (
              <div className={styles.notesBlock}>
                <p className={styles.notesTitle}>{t('recog.notes')}</p>
                <ul className={styles.notes}>
                  {review.notes.map((n, i) => (
                    <li key={i}>{n}</li>
                  ))}
                </ul>
              </div>
            )}
            {review.forms.length === 0 ? (
              <p className={styles.hint}>{t('recog.nothing')}</p>
            ) : (
              review.forms.map((f, i) => (
                <DraftCard key={i} index={i} form={f} errors={errorsList[i]} onChange={(patch) => updateForm(i, patch)} />
              ))
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

// LibraryPanel всё ещё импортирует старое имя — алиас, чтобы не трогать чужую зону лишний раз.
export { SourceModal as ParseModal };
